/**
 * Support form utility functions
 * 
 * Provides utilities for formatting location data and validating
 * support request form submissions.
 */

/**
 * Formats location data into a single display string
 * 
 * Combines address components (address, county, state, zip) into a
 * readable location string. Prioritizes full address if available.
 * 
 * @param locationAddress - Full address string (optional)
 * @param locationCounty - County name (optional)
 * @param locationState - State name (optional)
 * @param locationZipCode - ZIP code (optional)
 * @returns Formatted location string
 */
export const formatLocation = (
  locationAddress?: string,
  locationCounty?: string,
  locationState?: string,
  locationZipCode?: string,
): string => {
  if (locationAddress) {
    return locationAddress;
  }

  const parts: string[] = [];
  if (locationCounty) parts.push(locationCounty);
  if (locationState) parts.push(locationState);
  if (locationZipCode) parts.push(locationZipCode);

  return parts.length > 0 ? parts.join(', ') : '';
};

/**
 * Validates support request form data
 * 
 * Checks that all required fields are filled in:
 * - Email, name, primary person, urgency, description, preferred service, location
 * 
 * @param formData - Form data object to validate
 * @returns True if all required fields are present, false otherwise
 */
export const validateSupportForm = (formData: {
  email: string;
  name: string;
  primaryPerson: string;
  urgency: string;
  description: string;
  preferredService: string[];
  location: string;
}): boolean => {
  return !!(
    formData.email &&
    formData.name &&
    formData.primaryPerson &&
    formData.urgency &&
    formData.description &&
    formData.preferredService.length &&
    formData.location
  );
};

